/**
 * Agent in-app manual. Keep in sync with docs/manuals/AGENT_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const AGENT_MANUAL_INTRO = {
  title: "Agent guide",
  subtitle:
    "Agents handle outbound calling, queue execution, templates, call wrap-up, and reports. Focus on the agent sidebar workflow and use location context where available.",
  defaultRoute: "/agent",
};

export const AGENT_MANUAL_SECTIONS = [
  {
    id: "dashboard",
    group: "menu",
    menuLabel: "Dashboard",
    route: "/agent",
    keywords: ["home", "agent", "overview"],
    summary: "Agent workspace home with call and queue-related metrics.",
    howToUse: [
      "Open **Dashboard** (`/agent`).",
      "Review your queue and activity widgets before starting calls.",
    ],
  },
  {
    id: "lead-queue",
    group: "menu",
    menuLabel: "Lead Queue",
    route: "/agent/lead-queue",
    keywords: ["queue", "leads", "dialer", "calling", "sms", "wrap-up", "outcome"],
    summary:
      "Primary outbound workflow: dial leads, complete call wrap-up, send SMS from the lead panel, and log outcomes.",
    howToUse: [
      "Go to **Lead Queue** (`/agent/lead-queue`).",
      "Work top-priority leads; click to dial when the softphone is ready.",
      "After each call, complete wrap-up (outcome, notes) before moving to the next lead.",
      "Use the SMS panel on a lead to send template-based texts — this is separate from org [[Conversations|/conversations]] (AI follow-ups).",
    ],
    faqs: [
      {
        question: "How do I send SMS from Lead Queue?",
        answer:
          "Open [[Lead Queue|/agent/lead-queue]], select a lead, use the SMS section and pick a template from [[My Templates|/agent/my-templates]].",
      },
      {
        question: "What is the difference between Lead Queue and Conversations?",
        answer:
          "[[Lead Queue|/agent/lead-queue]] = agent outbound calling/SMS. [[Conversations|/conversations]] = org AI follow-up inbox for staff — different systems.",
      },
    ],
  },
  {
    id: "my-templates",
    group: "menu",
    menuLabel: "My Templates",
    route: "/agent/my-templates",
    keywords: ["sms", "template", "voicemail", "scripts"],
    summary: "Template manager for texts and related outreach content.",
    howToUse: [
      "Open **My Templates** (`/agent/my-templates`).",
      "Create or edit templates used during follow-ups and call workflows.",
    ],
  },
  {
    id: "call-history",
    group: "menu",
    menuLabel: "Call History",
    route: "/agent/call-history",
    keywords: ["history", "calls", "outcomes"],
    summary: "Review your recent calls and outcome tracking.",
    howToUse: [
      "Open **Call History** (`/agent/call-history`).",
      "Filter records by status/outcome and verify wrap-up quality.",
    ],
  },
  {
    id: "my-report",
    group: "menu",
    menuLabel: "My Report",
    route: "/reports",
    keywords: ["report", "performance", "metrics"],
    summary:
      "Performance reporting page shared with team leaders and super admins.",
    howToUse: [
      "Click **My Report** (`/reports`).",
      "Adjust report filters/date ranges to review your productivity and outcomes.",
    ],
  },
  {
    id: "profile",
    group: "account",
    menuLabel: "Your Profile",
    route: "/settings/profile",
    keywords: ["profile", "password", "account"],
    summary: "Your account and password settings.",
    howToUse: [
      "Use avatar menu → **Your Profile** (`/settings/profile`).",
      "Update personal details and password as needed.",
    ],
  },
  createHelpAssistantManualSection(),
];

export const AGENT_MANUAL_GROUPS = {
  menu: { label: "Main menu" },
  account: { label: "Account" },
  more: { label: "More features" },
};

export function filterAgentManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return AGENT_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
